import { Link } from "react-router-dom";
import {
  MapPin,
  Anchor,
  Sun,
  Calendar,
  TrendingUp,
  Users,
  ShoppingBag,
  Wind,
  Fish,
  Star,
  ArrowRight,
  Trees,
  Sailboat,
  Building2,
  Castle,
  Wheat,
  Sparkles,
  Waves,
} from "lucide-react";

/**
 * Generic CitySeoContent, accepts per-city SEO data and renders deep hyperlocal
 * content (neighborhoods, pricing, events, editorial approach, differentiators,
 * internal linking) optimized for top-1 Google ranking on "conciergerie <city>"
 * long-tails.
 *
 * Usage:
 *   <CitySeoContent city={city} seoData={CITY_SEO_DATA[slug]} />
 *
 * If seoData is null/undefined, the component renders nothing, the city
 * keeps its default (lighter) SEO setup.
 */

const ICON_MAP = {
  Anchor,
  MapPin,
  Sun,
  ShoppingBag,
  Wind,
  Fish,
  Calendar,
  Trees,
  Sailboat,
  Building2,
  Castle,
  Wheat,
  Sparkles,
  Waves,
  Users,
};

const CitySeoContent = ({ city, seoData }) => {
  if (!seoData) return null;
  const { intro, neighborhoods, pricing, events, editorial, differentiators, blogLinks } =
    seoData;

  return (
    <section
      className="py-20 lg:py-28 bg-white border-t border-gold/15"
      data-testid={`city-seo-content-${city.slug}`}
    >
      <div className="container-tm max-w-5xl">
        {/* Intro deep block */}
        <div className="mb-16">
          <span className="overline">
            Conciergerie Airbnb à {city.name} · le guide complet
          </span>
          <h2 className="mt-5 font-serif font-light text-ink text-4xl md:text-5xl lg:text-[52px] leading-[1.04] tracking-tight">
            {intro.headline}{" "}
            <span className="italic text-gold">{intro.headlineHighlight}</span>.
          </h2>
          <p
            className="mt-7 font-sans text-base md:text-lg text-ink/75 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: intro.text }}
          />
        </div>

        {/* Neighborhoods */}
        {neighborhoods && neighborhoods.length > 0 && (
          <div className="mb-20">
            <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight mb-3">
              Toutes les zones de {city.name} couvertes
            </h3>
            <p className="font-sans text-base text-ink/70 leading-relaxed mb-10 max-w-3xl">
              Notre équipe locale connaît parfaitement chaque secteur de {city.name}{" "}
              et adapte la stratégie commerciale à la signature de chaque zone.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {neighborhoods.map((n) => {
                const Icon = ICON_MAP[n.icon] || MapPin;
                return (
                  <div
                    key={n.name}
                    className="bg-cream/60 border border-gold/15 rounded-2xl p-6 hover:shadow-soft transition-shadow"
                  >
                    <div className="w-10 h-10 rounded-xl bg-gold/15 border border-gold/30 flex items-center justify-center mb-4">
                      <Icon size={18} strokeWidth={1.7} className="text-gold" />
                    </div>
                    <p className="font-serif text-lg text-ink leading-tight">
                      {n.name}
                    </p>
                    <p className="mt-3 font-sans text-sm text-ink/70 leading-relaxed">
                      {n.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pricing */}
        {pricing && pricing.length > 0 && (
          <div className="mb-20">
            <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight mb-3">
              Combien rapporte un Airbnb à {city.name} ?
            </h3>
            <p className="font-sans text-base text-ink/70 leading-relaxed mb-8 max-w-3xl">
              Voici les tarifs réellement constatés sur notre portefeuille à{" "}
              {city.name}, par typologie de bien. Le pricing dynamique permet de
              capter l'intégralité de la valeur en haute saison et lors des
              événements locaux.
            </p>
            <div className="overflow-x-auto rounded-2xl border border-gold/20 bg-white">
              <table className="w-full text-sm font-sans">
                <thead className="bg-cream">
                  <tr>
                    <th className="text-left px-5 py-3.5 font-semibold text-ink border-b border-gold/20">
                      Type de bien
                    </th>
                    <th className="text-left px-5 py-3.5 font-semibold text-ink border-b border-gold/20">
                      Basse saison
                    </th>
                    <th className="text-left px-5 py-3.5 font-semibold text-ink border-b border-gold/20">
                      Été
                    </th>
                    <th className="text-left px-5 py-3.5 font-semibold text-ink border-b border-gold/20">
                      Pics événementiels
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pricing.map((p, i) => (
                    <tr key={p.type} className={i % 2 ? "bg-cream/40" : ""}>
                      <td className="px-5 py-4 text-ink/85 border-b border-ink/5">
                        {p.type}
                      </td>
                      <td className="px-5 py-4 text-ink/75 border-b border-ink/5 tabular-nums">
                        {p.winter}
                      </td>
                      <td className="px-5 py-4 text-ink/75 border-b border-ink/5 tabular-nums">
                        {p.summer}
                      </td>
                      <td className="px-5 py-4 text-gold font-semibold border-b border-ink/5 tabular-nums">
                        {p.peak}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-5 text-xs font-sans text-ink/50 italic">
              Tarifs moyens constatés en 2025-2026 par nuit. Le potentiel réel
              dépend de la qualité des photos, des avis voyageurs et de la
              stratégie tarifaire mise en place.
            </p>
          </div>
        )}

        {/* Events */}
        {events && events.length > 0 && (
          <div className="mb-20">
            <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight mb-3">
              Les événements de {city.name} qu'il faut absolument exploiter
            </h3>
            <p className="font-sans text-base text-ink/70 leading-relaxed mb-10 max-w-3xl">
              Notre équipe pricing ajuste manuellement vos tarifs à chaque
              événement local. Manquer ces pics, c'est perdre 1 500 à 4 000 € par
              an selon le bien.
            </p>
            <div className="space-y-3">
              {events.map((e) => (
                <div
                  key={e.name}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 bg-sand/50 border border-gold/15 rounded-2xl p-5 lg:p-6"
                >
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-9 h-9 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center shrink-0 mt-0.5">
                      <Calendar size={15} strokeWidth={1.8} className="text-gold" />
                    </div>
                    <div>
                      <p className="font-serif text-lg text-ink leading-snug">
                        {e.name}
                      </p>
                      <p className="text-xs text-ink/55 mt-1 font-sans">{e.when}</p>
                    </div>
                  </div>
                  <div className="sm:max-w-[42%] sm:text-right">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-sage font-semibold flex sm:justify-end items-center gap-1.5">
                      <TrendingUp size={11} strokeWidth={2} />
                      Impact pricing
                    </p>
                    <p className="text-sm text-ink/75 font-sans mt-1.5 leading-snug">
                      {e.impact}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Editorial paragraphs */}
        {editorial && editorial.length > 0 && (
          <div className="mb-20">
            <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight mb-5">
              Notre approche éditoriale spécifique à {city.name}
            </h3>
            <div className="space-y-5 font-sans text-base md:text-lg text-ink/75 leading-relaxed">
              {editorial.map((p, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
              ))}
            </div>
            <div className="mt-10 p-7 bg-cream/60 border border-gold/20 rounded-2xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gold flex items-center justify-center shrink-0">
                  <Star size={20} strokeWidth={1.8} className="text-white" fill="white" />
                </div>
                <div>
                  <p className="font-serif text-xl text-ink leading-tight">
                    Résultat : +30 à 45 % de revenus en moyenne
                  </p>
                  <p className="mt-2 font-sans text-sm text-ink/70 leading-relaxed">
                    Les biens repris en pilotage chez Terre et Mer Conciergerie à{" "}
                    {city.name} gagnent en moyenne 30 à 45 % de revenus annuels la
                    première saison, grâce à notre méthode éditoriale + pricing
                    dynamique + multidiffusion sur Airbnb, Booking et Vrbo.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Differentiators */}
        {differentiators && differentiators.length > 0 && (
          <div className="mb-20">
            <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight mb-3">
              Pourquoi choisir Terre et Mer pour la conciergerie à {city.name} ?
            </h3>
            <div className="grid sm:grid-cols-2 gap-5 mt-8">
              {differentiators.map((d) => {
                const Icon = ICON_MAP[d.icon] || MapPin;
                return (
                  <div
                    key={d.title}
                    className="flex gap-5 bg-white border border-gold/15 rounded-2xl p-6"
                  >
                    <div className="w-11 h-11 rounded-xl bg-gold/15 border border-gold/30 flex items-center justify-center shrink-0">
                      <Icon size={18} strokeWidth={1.7} className="text-gold" />
                    </div>
                    <div>
                      <p className="font-serif text-lg text-ink leading-tight">
                        {d.title}
                      </p>
                      <p className="mt-2 font-sans text-sm text-ink/65 leading-relaxed">
                        {d.text}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Internal links */}
        {blogLinks && blogLinks.length > 0 && (
          <div className="bg-ink text-white rounded-3xl p-8 lg:p-12 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-[320px] h-[320px] bg-gold/15 rounded-full blur-[120px] pointer-events-none" />
            <div className="relative">
              <p className="overline text-gold mb-4">Pour aller plus loin</p>
              <p className="font-serif text-2xl md:text-3xl text-white leading-tight max-w-2xl mb-7">
                Nos articles dédiés à{" "}
                <span className="italic text-gold">{city.name} & alentour</span>
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {blogLinks.map((l) => (
                  <Link
                    key={l.url}
                    to={l.url}
                    className="flex items-center justify-between gap-3 bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-xl px-5 py-3.5 hover:bg-white/[0.07] transition-colors"
                  >
                    <span className="font-sans text-sm text-white">{l.label}</span>
                    <ArrowRight
                      size={14}
                      strokeWidth={2}
                      className="text-gold shrink-0"
                    />
                  </Link>
                ))}
              </div>
              <Link
                to="/contact"
                className="btn-accent group inline-flex mt-9"
                data-testid={`city-seo-cta-${city.slug}`}
              >
                Demander une étude pour mon bien à {city.name}
                <ArrowRight
                  size={14}
                  strokeWidth={2}
                  className="ml-3 group-hover:translate-x-1 transition-transform"
                />
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CitySeoContent;
